import {
	BSC_CHAIN_ID,
	FALLBACK_HANDLER_ADDRESS,
	MULTISEND_ADDRESS,
	SAFE_FACTORY_ADDRESS,
	SAFE_SINGLETON_ADDRESS,
} from '@/constants/bsc'
import { POLYGON_CHAIN_ID } from '@/constants/polymarket'
import { deriveSafe } from '@polymarket/builder-relayer-client/dist/builder/derive'
import { getContractConfig } from '@polymarket/builder-relayer-client/dist/config'
import { Contract, type Signer as EthersSigner, type providers, utils } from 'ethers'
import type { Address, Hex, PublicClient, WalletClient } from 'viem'
import { concat, encodeFunctionData, pad, toHex } from 'viem'

// ============================================================================
// Polygon Safe (existing)
// ============================================================================

// Derive Safe address from EOA - pure function, no hooks needed
export function deriveSafeAddress(eoaAddress: string): `0x${string}` | undefined {
	try {
		const config = getContractConfig(POLYGON_CHAIN_ID)
		return deriveSafe(eoaAddress, config.SafeContracts.SafeFactory) as `0x${string}`
	} catch (err) {
		console.error('Error deriving Safe address:', err)
		return undefined
	}
}

// ============================================================================
// BSC Safe derivation
// ============================================================================

/**
 * Derive Safe address for BSC - uses same logic as Polymarket
 * The SafeFactory address is the same across EVM chains (deterministic deployment)
 */
export function deriveBscSafeAddress(eoaAddress: string): `0x${string}` | undefined {
	try {
		return deriveSafe(eoaAddress, SAFE_FACTORY_ADDRESS) as `0x${string}`
	} catch (err) {
		console.error('Error deriving BSC Safe address:', err)
		return undefined
	}
}

/**
 * Check if Safe is deployed at address on any chain
 */
export async function isSafeDeployed(
	publicClient: PublicClient,
	safeAddress: Address
): Promise<boolean> {
	const code = await publicClient.getCode({ address: safeAddress })
	return code !== undefined && code !== '0x'
}

// ============================================================================
// ABIs
// ============================================================================

const SAFE_FACTORY_ABI = [
	{
		name: 'createProxyWithNonce',
		type: 'function',
		inputs: [
			{ name: '_singleton', type: 'address' },
			{ name: 'initializer', type: 'bytes' },
			{ name: 'saltNonce', type: 'uint256' },
		],
		outputs: [{ name: 'proxy', type: 'address' }],
	},
] as const

const SAFE_ABI = [
	{
		name: 'execTransaction',
		type: 'function',
		inputs: [
			{ name: 'to', type: 'address' },
			{ name: 'value', type: 'uint256' },
			{ name: 'data', type: 'bytes' },
			{ name: 'operation', type: 'uint8' },
			{ name: 'safeTxGas', type: 'uint256' },
			{ name: 'baseGas', type: 'uint256' },
			{ name: 'gasPrice', type: 'uint256' },
			{ name: 'gasToken', type: 'address' },
			{ name: 'refundReceiver', type: 'address' },
			{ name: 'signatures', type: 'bytes' },
		],
		outputs: [{ name: 'success', type: 'bool' }],
	},
	{
		name: 'nonce',
		type: 'function',
		inputs: [],
		outputs: [{ name: '', type: 'uint256' }],
		stateMutability: 'view',
	},
	{
		name: 'getTransactionHash',
		type: 'function',
		inputs: [
			{ name: 'to', type: 'address' },
			{ name: 'value', type: 'uint256' },
			{ name: 'data', type: 'bytes' },
			{ name: 'operation', type: 'uint8' },
			{ name: 'safeTxGas', type: 'uint256' },
			{ name: 'baseGas', type: 'uint256' },
			{ name: 'gasPrice', type: 'uint256' },
			{ name: 'gasToken', type: 'address' },
			{ name: 'refundReceiver', type: 'address' },
			{ name: '_nonce', type: 'uint256' },
		],
		outputs: [{ name: '', type: 'bytes32' }],
		stateMutability: 'view',
	},
] as const

const MULTISEND_ABI_VIEM = [
	{
		name: 'multiSend',
		type: 'function',
		inputs: [{ name: 'transactions', type: 'bytes' }],
		outputs: [],
	},
] as const

const ERC20_APPROVE_ABI = [
	{
		name: 'approve',
		type: 'function',
		inputs: [
			{ name: 'spender', type: 'address' },
			{ name: 'amount', type: 'uint256' },
		],
		outputs: [{ name: '', type: 'bool' }],
	},
] as const

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000' as Address
const MAX_UINT256 = BigInt(2) ** BigInt(256) - BigInt(1)

// ============================================================================
// Viem-based Safe deployment (WalletClient/PublicClient)
// ============================================================================

/**
 * Deploy Safe for user on BSC
 */
export async function deployBscSafe(
	walletClient: WalletClient,
	eoaAddress: Address
): Promise<`0x${string}`> {
	const initializer = encodeFunctionData({
		abi: [
			{
				name: 'setup',
				type: 'function',
				inputs: [
					{ name: '_owners', type: 'address[]' },
					{ name: '_threshold', type: 'uint256' },
					{ name: 'to', type: 'address' },
					{ name: 'data', type: 'bytes' },
					{ name: 'fallbackHandler', type: 'address' },
					{ name: 'paymentToken', type: 'address' },
					{ name: 'payment', type: 'uint256' },
					{ name: 'paymentReceiver', type: 'address' },
				],
				outputs: [],
			},
		],
		functionName: 'setup',
		args: [
			[eoaAddress],
			BigInt(1),
			ZERO_ADDRESS,
			'0x',
			FALLBACK_HANDLER_ADDRESS as Address,
			ZERO_ADDRESS,
			BigInt(0),
			ZERO_ADDRESS,
		],
	})

	const hash = await walletClient.writeContract({
		address: SAFE_FACTORY_ADDRESS as Address,
		abi: SAFE_FACTORY_ABI,
		functionName: 'createProxyWithNonce',
		args: [SAFE_SINGLETON_ADDRESS as Address, initializer, BigInt(0)],
		account: eoaAddress,
		// biome-ignore lint/suspicious/noExplicitAny: Chain requires flexible typing
		chain: { id: BSC_CHAIN_ID } as any,
	})

	return hash
}

/**
 * Encode a transaction for MultiSend (viem)
 */
function encodeMultiSendTx(to: Address, value: bigint, data: Hex): Hex {
	return concat([
		toHex(0, { size: 1 }), // operation: CALL
		to,
		pad(toHex(value), { size: 32 }),
		pad(toHex(BigInt((data.length - 2) / 2)), { size: 32 }),
		data,
	])
}

function createApproveData(spender: Address): Hex {
	return encodeFunctionData({
		abi: ERC20_APPROVE_ABI,
		functionName: 'approve',
		args: [spender, MAX_UINT256],
	})
}

/**
 * Execute a transaction via Safe with EOA signature
 */
export async function executeSafeTransaction(
	walletClient: WalletClient,
	publicClient: PublicClient,
	safeAddress: Address,
	eoaAddress: Address,
	to: Address,
	data: Hex,
	value = BigInt(0)
): Promise<`0x${string}`> {
	const nonce = await publicClient.readContract({
		address: safeAddress,
		abi: SAFE_ABI,
		functionName: 'nonce',
	})

	const txHash = await publicClient.readContract({
		address: safeAddress,
		abi: SAFE_ABI,
		functionName: 'getTransactionHash',
		args: [to, value, data, 0, BigInt(0), BigInt(0), BigInt(0), ZERO_ADDRESS, ZERO_ADDRESS, nonce],
	})

	const signature = await walletClient.signMessage({
		account: eoaAddress,
		message: { raw: txHash as Hex },
	})

	// Adjust v for Safe's eth_sign format (+4)
	const sigBytes = signature as Hex
	const v = Number.parseInt(sigBytes.slice(-2), 16)
	const adjustedSignature = (sigBytes.slice(0, -2) + (v + 4).toString(16)) as Hex

	const hash = await walletClient.writeContract({
		address: safeAddress,
		abi: SAFE_ABI,
		functionName: 'execTransaction',
		args: [
			to,
			value,
			data,
			0,
			BigInt(0),
			BigInt(0),
			BigInt(0),
			ZERO_ADDRESS,
			ZERO_ADDRESS,
			adjustedSignature,
		],
		account: eoaAddress,
		// biome-ignore lint/suspicious/noExplicitAny: Chain requires flexible typing
		chain: { id: BSC_CHAIN_ID } as any,
	})

	return hash
}

/**
 * Execute multiple Safe transactions via MultiSend
 */
export async function executeSafeMultiSend(
	walletClient: WalletClient,
	publicClient: PublicClient,
	safeAddress: Address,
	eoaAddress: Address,
	transactions: { to: Address; data: Hex; value?: bigint }[]
): Promise<`0x${string}`> {
	const encodedTxs = transactions.map(tx =>
		encodeMultiSendTx(tx.to, tx.value ?? BigInt(0), tx.data)
	)
	const multiSendData = concat(encodedTxs)

	const multiSendCallData = encodeFunctionData({
		abi: MULTISEND_ABI_VIEM,
		functionName: 'multiSend',
		args: [multiSendData],
	})

	const nonce = await publicClient.readContract({
		address: safeAddress,
		abi: SAFE_ABI,
		functionName: 'nonce',
	})

	const txHash = await publicClient.readContract({
		address: safeAddress,
		abi: SAFE_ABI,
		functionName: 'getTransactionHash',
		args: [
			MULTISEND_ADDRESS as Address,
			BigInt(0),
			multiSendCallData,
			1, // DELEGATECALL
			BigInt(0),
			BigInt(0),
			BigInt(0),
			ZERO_ADDRESS,
			ZERO_ADDRESS,
			nonce,
		],
	})

	const signature = await walletClient.signMessage({
		account: eoaAddress,
		message: { raw: txHash as Hex },
	})

	const sigBytes = signature as Hex
	const v = Number.parseInt(sigBytes.slice(-2), 16)
	const adjustedSignature = (sigBytes.slice(0, -2) + (v + 4).toString(16)) as Hex

	const hash = await walletClient.writeContract({
		address: safeAddress,
		abi: SAFE_ABI,
		functionName: 'execTransaction',
		args: [
			MULTISEND_ADDRESS as Address,
			BigInt(0),
			multiSendCallData,
			1, // DELEGATECALL
			BigInt(0),
			BigInt(0),
			BigInt(0),
			ZERO_ADDRESS,
			ZERO_ADDRESS,
			adjustedSignature,
		],
		account: eoaAddress,
		// biome-ignore lint/suspicious/noExplicitAny: Chain requires flexible typing
		chain: { id: BSC_CHAIN_ID } as any,
	})

	return hash
}

/**
 * Deploy Safe and approve tokens in two steps
 */
export async function deployBscSafeWithApprovals(
	walletClient: WalletClient,
	publicClient: PublicClient,
	eoaAddress: Address,
	quoteTokenAddress: Address,
	spenderAddresses: Address[]
): Promise<{ deployHash: `0x${string}`; approvalHash: `0x${string}` }> {
	const deployHash = await deployBscSafe(walletClient, eoaAddress)
	await publicClient.waitForTransactionReceipt({ hash: deployHash })

	const safeAddress = deriveBscSafeAddress(eoaAddress)
	if (!safeAddress) {
		throw new Error('Failed to derive Safe address')
	}

	const transactions = spenderAddresses.map(spender => ({
		to: quoteTokenAddress,
		data: createApproveData(spender),
	}))

	const approvalHash = await executeSafeMultiSend(
		walletClient,
		publicClient,
		safeAddress,
		eoaAddress,
		transactions
	)

	return { deployHash, approvalHash }
}

// ============================================================================
// Ethers-based Safe operations (for ViemEthersSigner)
// ============================================================================

const ETHERS_SAFE_ABI = [
	'function nonce() view returns (uint256)',
	'function getTransactionHash(address to, uint256 value, bytes calldata data, uint8 operation, uint256 safeTxGas, uint256 baseGas, uint256 gasPrice, address gasToken, address refundReceiver, uint256 _nonce) view returns (bytes32)',
	'function execTransaction(address to, uint256 value, bytes calldata data, uint8 operation, uint256 safeTxGas, uint256 baseGas, uint256 gasPrice, address gasToken, address refundReceiver, bytes memory signatures) payable returns (bool success)',
]

const ETHERS_SAFE_FACTORY_ABI = [
	'function createProxyWithNonce(address _singleton, bytes memory initializer, uint256 saltNonce) returns (address proxy)',
	'function createProxy(address paymentToken, uint256 payment, address paymentReceiver, (uint8 v, bytes32 r, bytes32 s) createSig) returns (address proxy)',
]

const ETHERS_MULTISEND_ABI = ['function multiSend(bytes memory transactions)']
const ETHERS_ERC20_ABI = ['function approve(address spender, uint256 amount) returns (bool)']

/**
 * Create EIP-712 signature for Safe creation
 */
export async function createSafeCreateSignature(
	walletClient: WalletClient,
	_ownerAddress: Address,
	chainId: number
): Promise<{ v: number; r: string; s: string }> {
	const domain = {
		name: 'Polymarket Contract Proxy Factory',
		chainId: BigInt(chainId),
		verifyingContract: SAFE_FACTORY_ADDRESS as Address,
	}

	const types = {
		CreateProxy: [
			{ name: 'paymentToken', type: 'address' },
			{ name: 'payment', type: 'uint256' },
			{ name: 'paymentReceiver', type: 'address' },
		],
	}

	const message = {
		paymentToken: ZERO_ADDRESS,
		payment: BigInt(0),
		paymentReceiver: ZERO_ADDRESS,
	}

	if (!walletClient.account) {
		throw new Error('WalletClient must have an account')
	}

	const signature = await walletClient.signTypedData({
		account: walletClient.account,
		domain,
		types,
		primaryType: 'CreateProxy',
		message,
	})

	const r = `0x${signature.slice(2, 66)}`
	const s = `0x${signature.slice(66, 130)}`
	const v = Number.parseInt(signature.slice(130, 132), 16)

	return { v, r, s }
}

/**
 * Deploy Safe using ethers signer with Polymarket factory's createProxy
 */
export async function deployBscSafeWithEthers(
	deployerSigner: EthersSigner,
	ownerSignature: { v: number; r: string; s: string }
): Promise<string> {
	const factory = new Contract(SAFE_FACTORY_ADDRESS, ETHERS_SAFE_FACTORY_ABI, deployerSigner)

	const tx = await factory.createProxy(
		ZERO_ADDRESS,
		0,
		ZERO_ADDRESS,
		[ownerSignature.v, ownerSignature.r, ownerSignature.s],
		{ gasLimit: 500000, gasPrice: 100000000 }
	)

	return tx.hash
}

/**
 * Encode a transaction for MultiSend using ethers
 */
function encodeEthersMultiSendTx(to: string, value: bigint, data: string): string {
	const dataLength = (data.length - 2) / 2
	return utils.solidityPack(
		['uint8', 'address', 'uint256', 'uint256', 'bytes'],
		[0, to, value.toString(), dataLength, data]
	)
}

function createEthersApproveData(spender: string): string {
	const erc20Interface = new utils.Interface(ETHERS_ERC20_ABI)
	return erc20Interface.encodeFunctionData('approve', [spender, MAX_UINT256.toString()])
}

/**
 * Execute Safe transaction with ethers signer
 */
export async function executeSafeTransactionWithEthers(
	signer: EthersSigner,
	safeAddress: string,
	_eoaAddress: string,
	to: string,
	data: string,
	value = BigInt(0),
	operation = 0
): Promise<string> {
	const safe = new Contract(safeAddress, ETHERS_SAFE_ABI, signer)

	const nonce = await safe.nonce()

	const txHash = await safe.getTransactionHash(
		to,
		value.toString(),
		data,
		operation,
		0,
		0,
		0,
		ZERO_ADDRESS,
		ZERO_ADDRESS,
		nonce
	)

	const signature = await signer.signMessage(utils.arrayify(txHash))

	// Adjust v for Safe's eth_sign format (+4)
	const sigBytes = utils.arrayify(signature)
	sigBytes[sigBytes.length - 1] += 4
	const adjustedSignature = utils.hexlify(sigBytes)

	const tx = await safe.execTransaction(
		to,
		value.toString(),
		data,
		operation,
		0,
		0,
		0,
		ZERO_ADDRESS,
		ZERO_ADDRESS,
		adjustedSignature
	)

	return tx.hash
}

/**
 * Execute multiple Safe transactions via MultiSend using ethers
 */
export async function executeSafeMultiSendWithEthers(
	signer: EthersSigner,
	safeAddress: string,
	eoaAddress: string,
	transactions: { to: string; data: string; value?: bigint }[]
): Promise<string> {
	const encodedTxs = transactions.map(tx =>
		encodeEthersMultiSendTx(tx.to, tx.value ?? BigInt(0), tx.data)
	)
	const multiSendData = utils.hexConcat(encodedTxs)

	const multiSendInterface = new utils.Interface(ETHERS_MULTISEND_ABI)
	const multiSendCallData = multiSendInterface.encodeFunctionData('multiSend', [multiSendData])

	return executeSafeTransactionWithEthers(
		signer,
		safeAddress,
		eoaAddress,
		MULTISEND_ADDRESS,
		multiSendCallData,
		BigInt(0),
		1 // DELEGATECALL
	)
}

/**
 * Execute Safe transaction with pre-signed signature
 */
export async function executeSafeTransactionWithSignature(
	executorSigner: EthersSigner,
	safeAddress: string,
	to: string,
	data: string,
	ownerSignature: { v: number; r: string; s: string },
	operation = 0,
	value = BigInt(0)
): Promise<string> {
	const safe = new Contract(safeAddress, ETHERS_SAFE_ABI, executorSigner)

	const adjustedV = ownerSignature.v >= 27 ? ownerSignature.v : ownerSignature.v + 27

	const signatureBytes = utils.solidityPack(
		['bytes32', 'bytes32', 'uint8'],
		[ownerSignature.r, ownerSignature.s, adjustedV]
	)

	const tx = await safe.execTransaction(
		to,
		value.toString(),
		data,
		operation,
		0,
		0,
		0,
		ZERO_ADDRESS,
		ZERO_ADDRESS,
		signatureBytes,
		{ gasLimit: 500000, gasPrice: 100000000 }
	)

	return tx.hash
}

/**
 * Get Safe transaction hash for signing
 */
export async function getSafeTransactionHash(
	provider: providers.Provider,
	safeAddress: string,
	to: string,
	data: string,
	operation = 0,
	value = BigInt(0)
): Promise<{ hash: string; nonce: number }> {
	const safe = new Contract(safeAddress, ETHERS_SAFE_ABI, provider)

	const nonce = await safe.nonce()

	const txHash = await safe.getTransactionHash(
		to,
		value.toString(),
		data,
		operation,
		0,
		0,
		0,
		ZERO_ADDRESS,
		ZERO_ADDRESS,
		nonce
	)

	return { hash: txHash, nonce: nonce.toNumber() }
}

/**
 * Build MultiSend data for token approvals
 */
export function buildApprovalMultiSendData(
	quoteTokenAddress: string,
	spenderAddresses: string[]
): string {
	const transactions = spenderAddresses.map(spender => ({
		to: quoteTokenAddress,
		data: createEthersApproveData(spender),
		value: BigInt(0),
	}))

	const encodedTxs = transactions.map(tx => encodeEthersMultiSendTx(tx.to, tx.value, tx.data))
	const multiSendData = utils.hexConcat(encodedTxs)

	const multiSendInterface = new utils.Interface(ETHERS_MULTISEND_ABI)
	return multiSendInterface.encodeFunctionData('multiSend', [multiSendData])
}

/**
 * Check if BSC Safe has required token approvals
 */
export async function checkBscApprovals(
	publicClient: PublicClient,
	safeAddress: Address,
	quoteTokenAddress: Address,
	spenderAddresses: Address[]
): Promise<{ allApproved: boolean; approvals: boolean[] }> {
	const ERC20_ALLOWANCE_ABI = [
		{
			name: 'allowance',
			type: 'function',
			inputs: [
				{ name: 'owner', type: 'address' },
				{ name: 'spender', type: 'address' },
			],
			outputs: [{ name: '', type: 'uint256' }],
			stateMutability: 'view',
		},
	] as const

	const threshold = BigInt('1000000000000')

	try {
		const allowances = await Promise.all(
			spenderAddresses.map(spender =>
				publicClient.readContract({
					address: quoteTokenAddress,
					abi: ERC20_ALLOWANCE_ABI,
					functionName: 'allowance',
					args: [safeAddress, spender],
				})
			)
		)

		const approvals = allowances.map(allowance => allowance >= threshold)
		return {
			allApproved: approvals.every(Boolean),
			approvals,
		}
	} catch (error) {
		console.error('[Safe] Error checking approvals:', error)
		return { allApproved: false, approvals: spenderAddresses.map(() => false) }
	}
}

/**
 * Deploy Safe and approve tokens (ethers version)
 */
export async function deployBscSafeWithApprovalsEthers(
	deployerSigner: EthersSigner,
	createProxySignature: { v: number; r: string; s: string },
	approvalSignature: { v: number; r: string; s: string },
	eoaAddress: string,
	quoteTokenAddress: string,
	spenderAddresses: string[],
	waitForReceipt: (hash: string) => Promise<void>
): Promise<{ deployHash: string; approvalHash: string }> {
	const deployHash = await deployBscSafeWithEthers(deployerSigner, createProxySignature)
	await waitForReceipt(deployHash)

	const safeAddress = deriveBscSafeAddress(eoaAddress)
	if (!safeAddress) {
		throw new Error('Failed to derive Safe address')
	}

	const multiSendData = buildApprovalMultiSendData(quoteTokenAddress, spenderAddresses)

	const approvalHash = await executeSafeTransactionWithSignature(
		deployerSigner,
		safeAddress,
		MULTISEND_ADDRESS,
		multiSendData,
		approvalSignature,
		1 // DELEGATECALL for MultiSend
	)

	return { deployHash, approvalHash }
}
